/**
 * MCP Documentation Tool Handlers
 *
 * Implementations for EnergyPlus documentation search and retrieval tools.
 */

import type { MCPToolResult } from '../types';
import type { HandlerContext } from './readHandlers';
import {
  searchDocs,
  searchDocsFiltered,
  isSearchIndexReady,
} from '@/docs/services/searchIndex';
import {
  getEngineeringDoc,
  getEngineeringDocs,
} from '@/docs/services/engineeringReference';
import type { IDDSchema } from '@/idd/types';

/**
 * Get object documentation from IDD schema
 */
function getObjectDocumentation(
  objectType: string,
  schema: IDDSchema
): {
  name: string;
  group: string | undefined;
  memo: string | undefined;
  fields: Array<{
    id: string;
    name: string;
    type: string;
    required: boolean;
    memo: string | undefined;
    default: string | undefined;
    units: string | undefined;
    minimum: number | string | undefined;
    maximum: number | string | undefined;
    choices: string[] | undefined;
    autosizable: boolean | undefined;
    autocalculatable: boolean | undefined;
  }>;
  isUnique: boolean | undefined;
  isRequired: boolean | undefined;
  relatedObjects: string[];
} | null {
  const objDef = schema.objectTypes.get(objectType.toLowerCase());
  if (!objDef) return null;

  const fields = objDef.fields.map((f) => ({
    id: f.id,
    name: f.name || f.id,
    type: f.type,
    required: f.required,
    memo: f.memo,
    default: f.default,
    units: f.units,
    minimum: f.minimum,
    maximum: f.maximum,
    choices: f.choices,
    autosizable: f.autosizable,
    autocalculatable: f.autocalculatable,
  }));

  // Find related objects via reference lists
  const relatedObjects: string[] = [];
  for (const field of objDef.fields) {
    if (field.objectList) {
      const referencedTypes = schema.referenceLists.get(field.objectList);
      if (referencedTypes) {
        for (const typeName of referencedTypes) {
          if (
            typeName.toLowerCase() !== objectType.toLowerCase() &&
            !relatedObjects.includes(typeName)
          ) {
            relatedObjects.push(typeName);
          }
        }
      }
    }
  }

  return {
    name: objDef.name,
    group: objDef.group,
    memo: objDef.memo,
    fields,
    isUnique: objDef.isUnique,
    isRequired: objDef.isRequired,
    relatedObjects: relatedObjects.slice(0, 10),
  };
}

/**
 * Get field documentation from IDD schema
 */
function getFieldDocumentation(
  objectType: string,
  fieldName: string,
  schema: IDDSchema
): {
  id: string;
  name: string;
  type: string;
  required: boolean;
  memo: string | undefined;
  default: string | undefined;
  units: string | undefined;
  minimum: number | string | undefined;
  maximum: number | string | undefined;
  choices: string[] | undefined;
  autosizable: boolean | undefined;
  autocalculatable: boolean | undefined;
  objectList: string | undefined;
} | null {
  const objDef = schema.objectTypes.get(objectType.toLowerCase());
  if (!objDef) return null;

  const field = objDef.fields.find(
    (f) =>
      f.name?.toLowerCase() === fieldName.toLowerCase() ||
      f.id.toLowerCase() === fieldName.toLowerCase()
  );
  if (!field) return null;

  return {
    id: field.id,
    name: field.name || field.id,
    type: field.type,
    required: field.required,
    memo: field.memo,
    default: field.default,
    units: field.units,
    minimum: field.minimum,
    maximum: field.maximum,
    choices: field.choices,
    autosizable: field.autosizable,
    autocalculatable: field.autocalculatable,
    objectList: field.objectList,
  };
}

/**
 * Handle search_documentation tool
 */
export function handleSearchDocumentation(
  args: Record<string, unknown>,
  _ctx: HandlerContext
): MCPToolResult {
  const query = args['query'] as string;
  const type = args['type'] as 'all' | 'object' | 'field' | 'engineering' | undefined;
  const limit = (args['limit'] as number) || 10;

  if (!query || query.trim().length === 0) {
    return {
      success: false,
      error: 'Search query is required',
      isError: true,
    };
  }

  if (!isSearchIndexReady()) {
    return {
      success: false,
      error: 'Documentation search index is not ready. The IDD schema may still be loading.',
      isError: true,
    };
  }

  const results =
    type && type !== 'all'
      ? searchDocsFiltered(query, { type, limit })
      : searchDocs(query, limit);

  return {
    success: true,
    content: {
      query,
      resultCount: results.length,
      results: results.map((r) => ({
        objectType: r.objectType,
        fieldName: r.fieldName,
        type: r.type,
        title: r.title,
        group: r.group,
        score: r.score,
      })),
    },
  };
}

/**
 * Handle get_object_documentation tool
 */
export function handleGetObjectDocumentation(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  const objectType = args['objectType'] as string;
  const includeFields = args['includeFields'] !== false;

  if (!objectType) {
    return {
      success: false,
      error: 'Object type is required',
      isError: true,
    };
  }

  if (!ctx.schema) {
    return {
      success: false,
      error: 'IDD schema is not loaded',
      isError: true,
    };
  }

  const doc = getObjectDocumentation(objectType, ctx.schema);

  if (!doc) {
    // Try to find similar object types
    const lowerQuery = objectType.toLowerCase();
    const suggestions: string[] = [];
    for (const [key] of ctx.schema.objectTypes) {
      if (key.includes(lowerQuery) || lowerQuery.includes(key)) {
        const objType = ctx.schema.objectTypes.get(key);
        if (objType) {
          suggestions.push(objType.name);
        }
        if (suggestions.length >= 5) break;
      }
    }

    return {
      success: false,
      error: `Object type not found: ${objectType}`,
      content: {
        suggestions: suggestions.length > 0 ? suggestions : undefined,
      },
      isError: true,
    };
  }

  // Optionally exclude detailed field information for shorter responses
  const response = includeFields
    ? doc
    : {
        name: doc.name,
        group: doc.group,
        memo: doc.memo,
        fieldCount: doc.fields.length,
        isUnique: doc.isUnique,
        isRequired: doc.isRequired,
        relatedObjects: doc.relatedObjects,
      };

  return {
    success: true,
    content: response,
  };
}

/**
 * Handle get_field_documentation tool
 */
export function handleGetFieldDocumentation(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  const objectType = args['objectType'] as string;
  const fieldName = args['fieldName'] as string;

  if (!objectType) {
    return {
      success: false,
      error: 'Object type is required',
      isError: true,
    };
  }

  if (!fieldName) {
    return {
      success: false,
      error: 'Field name is required',
      isError: true,
    };
  }

  if (!ctx.schema) {
    return {
      success: false,
      error: 'IDD schema is not loaded',
      isError: true,
    };
  }

  // Check if object exists
  const objDef = ctx.schema.objectTypes.get(objectType.toLowerCase());
  if (!objDef) {
    return {
      success: false,
      error: `Object type not found: ${objectType}`,
      isError: true,
    };
  }

  const doc = getFieldDocumentation(objectType, fieldName, ctx.schema);

  if (!doc) {
    // Provide available field names as suggestions
    const availableFields = objDef.fields.slice(0, 10).map((f) => f.name || f.id);
    return {
      success: false,
      error: `Field not found: ${fieldName} in ${objectType}`,
      content: {
        availableFields,
        totalFields: objDef.fields.length,
      },
      isError: true,
    };
  }

  return {
    success: true,
    content: {
      objectType: objDef.name,
      field: doc,
    },
  };
}

/**
 * Handle get_engineering_reference tool
 */
export function handleGetEngineeringReference(
  args: Record<string, unknown>,
  _ctx: HandlerContext
): MCPToolResult {
  const topic = args['topic'] as string;

  if (!topic) {
    return {
      success: false,
      error: 'Topic is required',
      isError: true,
    };
  }

  const doc = getEngineeringDoc(topic);

  if (!doc) {
    // List available topics
    const availableTopics = getEngineeringDocs().map((d) => ({
      id: d.id,
      title: d.title,
      category: d.category,
    }));

    return {
      success: false,
      error: `Engineering reference topic not found: ${topic}`,
      content: {
        availableTopics,
      },
      isError: true,
    };
  }

  return {
    success: true,
    content: {
      id: doc.id,
      title: doc.title,
      category: doc.category,
      content: doc.content,
      relatedObjects: doc.relatedObjects,
    },
  };
}

/**
 * Handle list_engineering_topics tool
 */
export function handleListEngineeringTopics(
  _args: Record<string, unknown>,
  _ctx: HandlerContext
): MCPToolResult {
  const docs = getEngineeringDocs();

  const topics = docs.map((doc) => ({
    id: doc.id,
    title: doc.title,
    category: doc.category,
    relatedObjects: doc.relatedObjects,
  }));

  return {
    success: true,
    content: {
      topicCount: topics.length,
      topics,
    },
  };
}

/**
 * All documentation handlers
 */
export const docsHandlers: Record<
  string,
  (args: Record<string, unknown>, ctx: HandlerContext) => MCPToolResult
> = {
  search_documentation: handleSearchDocumentation,
  get_object_documentation: handleGetObjectDocumentation,
  get_field_documentation: handleGetFieldDocumentation,
  get_engineering_reference: handleGetEngineeringReference,
  list_engineering_topics: handleListEngineeringTopics,
};
